// package
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

//---------------------------------------------------------------------------
// custom
import { ROUTES } from '@app/router/routes';
import { isAuthenticated, register } from '@features/auth/api/authService';
import { getUserInfo } from '@shared/api/client';
import { get34QuizChapterList } from '../api/quizService';
import { usePageLayout } from '@/app/router';
import CloseButton from '@shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';

/** UTC / 帶時區字串轉成本地時間戳（與篇章列表選預設邏輯一致） */
const utcToLocalMs = (s) => {
    if (s == null) return NaN;
    const str = String(s).trim();
    if (str.includes('GMT') || str.includes('Z') || /[+-]\d{2}:?\d{2}$/.test(str)) {
        return new Date(str).getTime();
    }
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(str) ? `${str}T00:00:00` : str.replace(' ', 'T');
    return new Date(normalized + 'Z').getTime();
};

// 預設篇章選項（API 載入失敗時的 fallback）
const DEFAULT_CHAPTER_OPTIONS = [
    { id: 1, title: '弘法行履篇', description: '2026-04-01 ~ 04-07' },
    { id: 2, title: '祖師教法篇', description: '2026-04-08 ~ 04-14' },
    { id: 3, title: '燈燈無盡篇', description: '2026-04-15 ~ 04-21' },
    { id: 4, title: '精舍弘法篇', description: '2026-04-22 ~ 04-28' },
    { id: 5, title: '中台庭園篇', description: '2026-04-22 ~ 04-28' },
    { id: 6, title: '中台建築篇', description: '2026-04-29 ~ 05-05' },
];

// const textHasIntegral = '本次測驗將計算積分';
const textHasIntegral = '';

/**
 * 教理問答開始頁面 - 對應 example/quiz_start.html
 * Quiz Start Page
 *
 * 注意：此頁面不需要包裝 PageLayout，因為 router 層級的 PageLayoutWrapper 已經提供。
 */
//---------------------------------------------------------------------------
const QuizListPage = ({
    heroData,
    onSubmit,
}) => {
    const navigate = useNavigate();

    const { setLayoutOptions } = usePageLayout();
    const { t, currentLanguage } = useLanguage();
    const isEn = currentLanguage.code === 'en';
    const resolvedHeroData = heroData ?? { image: '', title: t('quiz.heroTitle'), description: '' };
    const arrRuleText = [t('quiz.rule1'), t('quiz.rule2'), t('quiz.rule3'), t('quiz.rule4')];
    useEffect(() => {
        setLayoutOptions({ hideHeader: true, hideBottomNav: true, hideFooter: true });
        return () => setLayoutOptions({});  // 離開頁面時還原
    }, [setLayoutOptions]);

    // RWD：畫面寬度小於 420 時，規則說明區塊間距調整
    const [helpRulesGap, setHelpRulesGap] = useState(() => {
        if (typeof window === 'undefined') return 32;
        return window.innerWidth < 420 ? 56 : 32;
    });

    useEffect(() => {
        const handleResize = () => {
            setHelpRulesGap(window.innerWidth < 420 ? 56 : 32);
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // 確保初次進入頁面就套用正確值

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [chapterOptions, setChapterOptions] = useState(DEFAULT_CHAPTER_OPTIONS);
    const [hasIntegral, setHasIntegral] = useState(false);
    const [starting, setStarting] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        chapterId: null,
    });

    useEffect(() => {
        const userInfo = getUserInfo();
        if (userInfo?.name) {
            setFormData(prev => ({ ...prev, name: userInfo.name }));
        }

        // 篇章列表
        get34QuizChapterList()
            .then((res) => {
                const items = res?.data?.items ?? res?.items ?? [];
                if (items.length > 0) {
                    const options = items
                        .map((item) => {
                            const id = Number(item['chapter_id']);
                            const start_at = item['start_at'];
                            const end_at = item['end_at'];
                            const startAt = new Date(start_at).toLocaleDateString();
                            const endAt = new Date(end_at).toLocaleDateString();
                            const description = `${startAt} ~ ${endAt}`;
                            const isTestAllowed = item['is_available'] !== false;
                            const is_current = item['is_current'] === true;
                            const title = item['content'];
                            return {
                                id,
                                title,
                                start_at,
                                end_at,
                                description,
                                isTestAllowed,
                                is_current,
                            };
                        })
                        // API 僅回傳可作答週；依開始日排序，左箭頭＝上一週（較早）
                        .sort((a, b) => utcToLocalMs(a.start_at) - utcToLocalMs(b.start_at));

                    // console.log('options', options);
                    setChapterOptions(options);

                    const now = Date.now();
                    const inRangeOpts = options.filter((opt) => {
                        const inRange =
                            now >= utcToLocalMs(opt.start_at) && now <= utcToLocalMs(opt.end_at);
                        return inRange && opt.isTestAllowed;
                    });
                    // 預設當週：優先 is_current，否則目前時間落在區間內者，再否則第一筆
                    let selected =
                        options.find((opt) => opt.is_current) ??
                        inRangeOpts[0] ??
                        options[0];
                    const chapterId =
                        selected != null && Number.isFinite(Number(selected.id))
                            ? Number(selected.id)
                            : null;
                    setFormData((prev) => ({ ...prev, chapterId }));
                    setHasIntegral(selected?.is_current ?? false);
                }
            })
            .catch((err) => {
                console.error('Failed to load vow categories:', err);
            });
    }, []);

    const onClickBack = () => {
        navigate(-1);
    };
    /** 切換篇章時同步寫入 formData.chapterId（與積分旗標） */
    const selectChapterOption = (option) => {
        if (option == null) return;
        const id = Number(option.id);
        if (!Number.isFinite(id)) return;
        setFormData((prev) => ({ ...prev, chapterId: id }));
        setHasIntegral(option.is_current === true);
    };

    const selectedChapterIndex = chapterOptions.findIndex((o) => {
        if (formData.chapterId == null) return false;
        return Number(o.id) === Number(formData.chapterId);
    });
    const canGoPrev = selectedChapterIndex > 0;
    const canGoNext =
        selectedChapterIndex >= 0 && selectedChapterIndex < chapterOptions.length - 1;

    const onClickChapterPrev = () => {
        if (!canGoPrev) return;
        selectChapterOption(chapterOptions[selectedChapterIndex - 1]);
    };

    const onClickChapterNext = () => {
        if (!canGoNext) return;
        selectChapterOption(chapterOptions[selectedChapterIndex + 1]);
    };

    const onClickStart = async () => {
        if (starting || !formData.chapterId) return;

        setStarting(true);
        try {
            const chapterId = formData.chapterId;
            navigate(ROUTES.QUIZ_QUESTION(chapterId));
        } catch (err) {
            console.error('Failed to start quiz:', err);
            setStarting(false);
        }
    };

    //---------------------------------------------------------------------------
    const containerStyle = {
        width: '100%',
        maxWidth: 430,
        minHeight: 932,
        position: 'relative',
        background: '#F7F8FA',
        overflow: 'hidden',
        margin: '0 auto',
    };

    const headerGradientStyle = {
        background: 'linear-gradient(180deg, #4DC8E0 0%, #0097B2 100%)',
        boxShadow: '0px 4px 30px rgba(88.73, 187.23, 206.17, 0.50)',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: 20,
        borderBottomRightRadius: 20,
    };

    const chapterStripTop = (isEn ? 545 : 477) + 144; /* 144=header height */
    const chapterStripMinHeight = 66;
    const startButtonGap = 16;
    const startButtonTop = chapterStripTop + chapterStripMinHeight + startButtonGap;

    //---------------------------------------------------------------------------
    return (
        <section style={containerStyle}>
            {/* Header 漸層區塊（定位同 佛學會考.jsx） */}
            <div
                style={{
                    width: '100%',
                    height: 144,
                    paddingTop: 33.5,
                    paddingBottom: 33.5,
                    left: 0,
                    top: -1,
                    position: 'absolute',
                    ...headerGradientStyle,
                }}
            />

            {/* 右上角關閉/返回按鈕 */}
            <CloseButton onClick={onClickBack} />

            {/* 標題（定位同 佛學會考.jsx） */}
            <div
                style={{
                    width: '66.5%',
                    maxWidth: 286,
                    left: 21,
                    top: 32,
                    position: 'absolute',
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'white',
                    fontSize: isEn ? 18 : 30,
                    fontWeight: 900,
                    lineHeight: isEn ? '24px' : '36px',
                    wordWrap: 'break-word',
                }}
            >
                {resolvedHeroData.title}
            </div>

            {/* 遊戲說明（定位同 佛學會考.jsx） */}
            <div
                style={{
                    width: '66.5%',
                    maxWidth: 286,
                    height: 57,
                    left: 51,
                    top: 33 + 144, /* 144=header height */
                    position: 'absolute',
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    color: 'black',
                    fontSize: isEn ? 18 : 30,
                    fontWeight: 900,
                    lineHeight: isEn ? '24px' : '36px',
                    wordWrap: 'break-word',
                }}
            >
                {t('quiz.gameRulesTitle')}
            </div>

            {/* 測驗規則（定位同 佛學會考.jsx；內容寬度 RWD） */}
            <div
                style={{
                    // background: '#f00',
                    width: 'calc(100% - 64px)',
                    maxWidth: 366,
                    left: 32,
                    top: (isEn ? 110 : 138) + 144, /* 144=header height */
                    position: 'absolute',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    // gap: 16,
                    gap: isEn ? 0 : helpRulesGap,
                }}
            >
                {arrRuleText.map((text, idx) => (
                    <div
                        key={idx}
                        style={{
                            // background: '#0f0',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            // gap: 14,
                            gap: 10,
                        }}
                    >
                        <div
                            style={{
                                width: 28,
                                height: 28,
                                background: '#0097B2',
                                borderRadius: 14,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <div
                                style={{
                                    color: 'white',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    wordWrap: 'break-word',
                                }}
                            >
                                {idx + 1}
                            </div>
                        </div>
                        <div style={{ flex: '1 1 0', position: 'relative', minHeight: 26 }}>
                            <div
                                style={{
                                    left: 0,
                                    top: 0,
                                    position: isEn ? 'relative' : 'absolute',
                                    color: '#3A3A3A',
                                    fontSize: 17,
                                    fontWeight: 300,
                                    lineHeight: '26px',
                                    wordWrap: 'break-word',
                                }}
                            >
                                {text}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* 篇章週次切換（Frame 34556；位於原「遊戲開始」按鈕位置；API 僅回傳可作答週，左為上一週） */}
            <div
                className="quiz_form_section"
                style={{
                    width: 'calc(100% - 70px)',
                    maxWidth: 361,
                    left: 35,
                    top: chapterStripTop,
                    position: 'absolute',
                    minHeight: chapterStripMinHeight,
                    padding: '10px 7px',
                    boxSizing: 'border-box',
                    background: 'white',
                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.20)',
                    borderRadius: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                }}
            >
                <button
                    type="button"
                    aria-label={t('quiz.chapterPrevAria')}
                    disabled={!canGoPrev}
                    onClick={onClickChapterPrev}
                    style={{
                        width: 30,
                        height: 30,
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: canGoPrev ? 'pointer' : '',
                        opacity: canGoPrev ? 1 : 0.35,
                        flexShrink: 0,
                        // background: '#f00',
                    }}
                >
                    {canGoPrev
                        ? <ChevronLeft size={30} color="#1C1B1F" strokeWidth={2.5} aria-hidden />
                        : null}
                </button>
                <div
                    style={{
                        flex: '1 1 0',
                        minWidth: 0,
                        textAlign: 'center',
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        fontSize: '24px',
                        color: '#6E625B',
                        fontFamily: "'Noto Serif TC', 'Noto Sans TC', sans-serif",
                        fontWeight: 500,
                        wordWrap: 'break-word',
                        gap: 0,
                    }}
                >
                    {selectedChapterIndex >= 0
                        ? <>
                            <span style={{ fontSize: isEn ? '14px' : '18px', lineHeight: '20px' }}>{chapterOptions[selectedChapterIndex]?.description}</span>
                            <span style={{ fontSize: isEn ? '16px' : '24px', lineHeight: isEn ? '22px' : '26px' }}>{chapterOptions[selectedChapterIndex]?.title}</span>
                        </>
                        : t('quiz.comingSoon')}
                </div>
                <button
                    type="button"
                    aria-label={t('quiz.chapterNextAria')}
                    disabled={!canGoNext}
                    onClick={onClickChapterNext}
                    style={{
                        width: 30,
                        height: 30,
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: canGoNext ? 'pointer' : '',
                        opacity: canGoNext ? 1 : 0.35,
                        flexShrink: 0,
                        // background: '#f00',
                    }}
                >
                    {canGoNext
                        ? <ChevronRight size={30} color="#1C1B1F" strokeWidth={2.5} aria-hidden />
                        : null}
                </button>
            </div>

            {/* 遊戲開始按鈕（原位置改為篇章列後下移） */}
            <button
                type="button"
                onClick={onClickStart}
                disabled={!formData.chapterId || starting}
                style={{
                    width: 'calc(100% - 70px)',
                    maxWidth: 361,
                    height: 58,
                    left: 35,
                    top: startButtonTop,
                    position: 'absolute',
                    background: 'linear-gradient(180deg, #4DC8E0 0%, #0097B2 100%)',
                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.20)',
                    borderRadius: 20,
                    border: 'none',
                    cursor: !formData.chapterId || starting ? 'not-allowed' : 'pointer',
                    opacity: !formData.chapterId || starting ? 0.5 : 1,
                }}
                aria-label={t('quiz.startGame')}
            >
                <span
                    style={{
                        // background: '#f00',
                        width: '100%',
                        height: '100%',
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        color: 'white',
                        fontSize: 24,
                        fontWeight: 500,
                        lineHeight: '38px',
                        wordWrap: 'break-word',
                    }}
                >
                    {starting ? t('quiz.processing') : t('quiz.startGame')}
                    <ChevronRight size={24} color="#FFFFFF" strokeWidth={3} aria-hidden />
                </span>
            </button>
            {hasIntegral && (
                <div
                    style={{
                        // background: '#0f0',
                        width: 'calc(100% - 70px)',
                        maxWidth: 361,
                        height: 20,
                        left: 32,
                        top: startButtonTop + 58,
                        position: 'absolute',
                        textAlign: 'center',
                        fontSize: 18,
                        fontWeight: 300,
                        lineHeight: '28px',
                        wordWrap: 'break-word',
                    }}
                >
                    {textHasIntegral}
                </div>
            )}
        </section>
    );
};

QuizListPage.propTypes = {
    heroData: PropTypes.shape({
        image: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
    }),
    onSubmit: PropTypes.func,
};

export default QuizListPage;
